import React, { useState } from 'react';
import { Modal, Button, PasswordInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { usePasswordPolicy } from '@mygpt/app';
import {
  buildPasswordValidator,
  getEnabledPasswordRuleKeys,
  getPasswordPolicyErrorMessage,
  PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS,
} from '@mygpt/app/utils';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // 密码策略:legacy(未配置/拉取失败)时保持现状校验,策略已配置时按规则预校验
  const { legacy: policyLegacy, rules: policyRules } = usePasswordPolicy();
  const enabledRuleKeys = !policyLegacy && policyRules ? getEnabledPasswordRuleKeys(policyRules) : [];

  const handleSubmit = async () => {
    if (!oldPassword.trim() || !newPassword.trim() || !confirmNewPassword.trim()) {
      setError(t('adminConsole.employees.allFieldsRequired'));
      return;
    }
    if (policyLegacy || !policyRules) {
      // legacy 模式:保持现状(长度≥8 + 4 类字符取 3)
      if (newPassword.length < 8) {
        setError(t('login_password_min_length'));
        return;
      }
      {
        let categories = 0;
        if (/[a-z]/.test(newPassword)) categories++;
        if (/[A-Z]/.test(newPassword)) categories++;
        if (/[0-9]/.test(newPassword)) categories++;
        if (/[^a-zA-Z0-9]/.test(newPassword)) categories++;
        if (categories < 3) {
          setError(t('reset_password.password_too_weak'));
          return;
        }
      }
    } else {
      // 策略模式:按已配置规则预校验(min_length_8 / require_case_digit / require_special)
      const violations = buildPasswordValidator(policyRules)(newPassword);
      if (violations.length) {
        setError(violations.map((v) => t(PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS[v])).join('; '));
        return;
      }
    }
    if (newPassword !== confirmNewPassword) {
      setError(t('adminConsole.employees.passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await onSubmit(oldPassword, newPassword);

      if (result.success) {
        // 成功时清空表单并关闭弹窗
        setOldPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        onClose();
      } else {
        // 失败时显示错误信息，不关闭弹窗
        setError(result.error || t('adminConsole.employees.passwordChangeFailed'));
      }
    } catch (error) {
      // 处理异常情况;新错误码分支:PASSWORD_POLICY_VIOLATION / PASSWORD_REUSED(detail.code 判别)
      setError(getPasswordPolicyErrorMessage(error, t) || t('adminConsole.employees.passwordChangeFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal opened={isOpen} onClose={onClose} title={t('adminConsole.employees.changePassword')} centered>
      <div>
        <PasswordInput
          label={t('adminConsole.employees.oldPassword')}
          placeholder={t('adminConsole.employees.oldPasswordPlaceholder')}
          value={oldPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            // 过滤掉汉字，只允许英文字母、数字和特殊字符
            const filteredValue = e.target.value.replace(/[\u4e00-\u9fa5]/g, '');
            setOldPassword(filteredValue);
          }}
          required
        />
        <PasswordInput
          label={t('adminConsole.employees.newPassword')}
          placeholder={t('adminConsole.employees.newPasswordPlaceholder')}
          description={
            policyLegacy || !policyRules
              ? t('reset_password.password_requirements')
              : enabledRuleKeys.length > 0
              ? `${t('password_policy.hint_title')} ${enabledRuleKeys
                  .map((key) => t(PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS[key]))
                  .join('; ')}`
              : undefined
          }
          value={newPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            // 过滤掉汉字，只允许英文字母、数字和特殊字符
            const filteredValue = e.target.value.replace(/[\u4e00-\u9fa5]/g, '');
            setNewPassword(filteredValue);
          }}
          required
        />
        <PasswordInput
          label={t('adminConsole.employees.confirmNewPassword')}
          placeholder={t('adminConsole.employees.confirmNewPasswordPlaceholder')}
          value={confirmNewPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            // 过滤掉汉字，只允许英文字母、数字和特殊字符
            const filteredValue = e.target.value.replace(/[\u4e00-\u9fa5]/g, '');
            setConfirmNewPassword(filteredValue);
          }}
          required
        />
        {error && <div className="text-red-500 mt-2">{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button variant="outline" onClick={onClose} className="mr-2" disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" className="ml-2" onClick={handleSubmit} loading={isLoading}>
            {isLoading ? t('common.submitting') : t('common.saveChanges')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
